'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const pricingTiers = [
  {
    id: 'weekday',
    label: 'Weekday',
    badge: null,
    color: 'bg-card border-border',
    headerColor: 'bg-muted',
    items: [
      { name: 'Green Fee — 18 Holes', price: 'R 220' },
      { name: 'Green Fee — 9 Holes', price: 'R 140' },
      { name: 'Cart Hire — Single', price: 'R 180' },
      { name: 'Cart Hire — Double', price: 'R 360' },
    ],
    note: 'Monday–Friday, excluding public holidays',
  },
  {
    id: 'weekend',
    label: 'Weekend',
    badge: 'Most Popular',
    color: 'bg-primary border-primary',
    headerColor: 'gradient-primary',
    items: [
      { name: 'Green Fee — 18 Holes', price: 'R 280' },
      { name: 'Green Fee — 9 Holes', price: 'R 170' },
      { name: 'Cart Hire — Single', price: 'R 180' },
      { name: 'Cart Hire — Double', price: 'R 360' },
    ],
    note: 'Saturday, Sunday & public holidays',
  },
  {
    id: 'senior',
    label: 'Senior / Junior',
    badge: null,
    color: 'bg-card border-border',
    headerColor: 'bg-muted',
    items: [
      { name: 'Senior Green Fee — 18 Holes', price: 'R 180' },
      { name: 'Senior Green Fee — 9 Holes', price: 'R 110' },
      { name: 'Junior Green Fee — 18 Holes', price: 'R 120' },
      { name: 'Cart Hire — Single', price: 'R 180' },
    ],
    note: 'Seniors 60+ and juniors under 18',
  },
];

const extras = [
  { icon: 'WrenchScrewdriverIcon', label: 'Club Hire', value: 'R 80 / set' },
  { icon: 'ShoppingBagIcon', label: 'Caddie', value: 'R 120 + tip' },
  { icon: 'BuildingStorefrontIcon', label: 'Locker Room', value: 'Complimentary' },
  { icon: 'WifiIcon', label: 'Clubhouse Wi-Fi', value: 'Complimentary' },
];

export default function PricingSection() {
  const [activeTab, setActiveTab] = useState<'weekday' | 'weekend' | 'senior'>('weekend');
  const active = pricingTiers.find((t) => t.id === activeTab)!;

  return (
    <section id="pricing" className="py-20 bg-background">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-accent font-semibold text-sm tracking-widest uppercase">Green Fees</span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2">Transparent Pricing</h2>
          <p className="text-muted-foreground mt-3 max-w-lg mx-auto text-base">
            No hidden fees. All prices include course access and use of practice facilities.
          </p>
        </div>

        {/* Desktop: 3-column layout */}
        <div className="hidden lg:grid grid-cols-3 gap-6 mb-10">
          {pricingTiers.map((tier) => (
            <div
              key={tier.id}
              className={`rounded-2xl border-2 overflow-hidden transition-all duration-200 ${
                tier.id === 'weekend' ?'border-primary shadow-[0_8px_32px_rgba(27,67,50,0.18)] scale-[1.02]' :'border-border'
              }`}
            >
              {/* Card header */}
              <div className={`px-6 py-5 ${tier.id === 'weekend' ? 'gradient-primary' : 'bg-muted'}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`font-bold text-lg ${tier.id === 'weekend' ? 'text-white' : 'text-foreground'}`}>
                    {tier.label}
                  </span>
                  {tier.badge && (
                    <span className="bg-accent text-accent-foreground text-xs font-bold px-2.5 py-1 rounded-full">
                      {tier.badge}
                    </span>
                  )}
                </div>
                <p className={`text-xs ${tier.id === 'weekend' ? 'text-white/60' : 'text-muted-foreground'}`}>
                  {tier.note}
                </p>
              </div>

              {/* Pricing rows */}
              <div className="bg-card px-6 py-5 space-y-3">
                {tier.items.map((item) => (
                  <div key={item.name} className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{item.name}</span>
                    <span className="text-sm font-bold text-foreground tabular-nums">{item.price}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="bg-card px-6 pb-6">
                <Link
                  href="/online-booking"
                  className={`w-full text-center py-3 rounded-xl text-sm font-bold block transition-all duration-150 ${
                    tier.id === 'weekend' ?'btn-gold' :'border-2 border-primary text-primary hover:bg-primary hover:text-white'
                  }`}
                >
                  Book This Rate
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile: tab switcher */}
        <div className="lg:hidden mb-10">
          <div className="flex rounded-xl border border-border overflow-hidden mb-6">
            {pricingTiers.map((tier) => (
              <button
                key={tier.id}
                onClick={() => setActiveTab(tier.id as typeof activeTab)}
                className={`flex-1 py-2.5 text-sm font-semibold transition-all duration-150 ${
                  activeTab === tier.id
                    ? 'gradient-primary text-white' :'bg-card text-muted-foreground hover:text-foreground'
                }`}
              >
                {tier.label}
              </button>
            ))}
          </div>
          <div className="rounded-2xl border-2 border-primary overflow-hidden shadow-[0_4px_20px_rgba(27,67,50,0.12)]">
            <div className="gradient-primary px-6 py-5">
              <div className="flex items-center justify-between mb-1">
                <span className="font-bold text-lg text-white">{active.label}</span>
                {active.badge && (
                  <span className="bg-accent text-accent-foreground text-xs font-bold px-2.5 py-1 rounded-full">
                    {active.badge}
                  </span>
                )}
              </div>
              <p className="text-white/60 text-xs">{active.note}</p>
            </div>
            <div className="bg-card px-6 py-5 space-y-3">
              {active.items.map((item) => (
                <div key={item.name} className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{item.name}</span>
                  <span className="text-sm font-bold text-foreground tabular-nums">{item.price}</span>
                </div>
              ))}
            </div>
            <div className="bg-card px-6 pb-6">
              <Link href="/online-booking" className="btn-gold w-full text-center py-3 rounded-xl text-sm font-bold block">
                Book This Rate
              </Link>
            </div>
          </div>
        </div>

        {/* Extras strip */}
        <div className="rounded-2xl bg-muted border border-border px-6 py-5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-4">Additional Services</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {extras.map((e) => (
              <div key={e.label} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-card border border-border flex items-center justify-center shrink-0">
                  <Icon name={e.icon as Parameters<typeof Icon>[0]['name']} size={18} variant="outline" className="text-primary" />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">{e.label}</div>
                  <div className="text-sm font-semibold text-foreground">{e.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* VAT note */}
        <p className="text-center text-xs text-muted-foreground mt-5">
          All prices are in South African Rand (ZAR) and inclusive of VAT. Prices subject to change — confirm at time of booking.
        </p>
      </div>
    </section>
  );
}
